import { Injectable } from '@angular/core';
import { LimitedChatDocument } from '../../../models/chat-ui.model';

@Injectable({
  providedIn: 'root'
})
export class ChatStorageService {
  private readonly selectedMessageKey = 'chat.selected_message_entity_id';
  private readonly sessionKey = 'chat_session_id';
  private readonly limitedDocumentKey = 'limitChatDocId';
  private readonly lastRouteKey = 'lastRoute';
  private readonly selectedExpertRoleKey = 'chat.selected_expert_role';

  /**
   * Claves que sobreviven al borrado total de una sesión nueva (decisión fundador
   * 2026-07-21: limpiar el estado del CHAT, no desloguear ni romper preferencias).
   * - Prefijo `@@auth0spajs@@`: caché de tokens de Auth0 (`cacheLocation: 'localstorage'`).
   * - `theme` / `language` / `sidebar_collapsed`: preferencias de UI, no estado de chat.
   */
  private readonly preservedKeyPrefixes: readonly string[] = ['@@auth0spajs@@'];
  private readonly preservedKeys: readonly string[] = ['theme', 'language', 'sidebar_collapsed'];

  getSelectedMessageId(): string | null {
    return this.getItem(this.selectedMessageKey);
  }

  setSelectedMessageId(id: string): void {
    this.setItem(this.selectedMessageKey, id);
  }

  clearSelectedMessageId(): void {
    this.removeItem(this.selectedMessageKey);
  }

  getSessionId(): string | null {
    return this.getItem(this.sessionKey);
  }

  getOrCreateSessionId(): string {
    const current = this.getSessionId();
    if (current) {
      return current;
    }

    this.setSessionId('0');
    return '0';
  }

  setSessionId(id: string): void {
    this.setItem(this.sessionKey, id);
  }

  setLastRoute(route: string): void {
    this.setItem(this.lastRouteKey, route);
  }

  getLimitedDocument(): LimitedChatDocument | null {
    const value = this.getItem(this.limitedDocumentKey);
    if (!value) {
      return null;
    }

    const parts = value.split('|');
    return {
      value,
      id: parts[0] || null,
      name: parts.slice(1).join('|') || null
    };
  }

  setLimitedDocument(id: string, name: string): LimitedChatDocument {
    const safeName = name.replace(/\|/g, ' ');
    const value = `${id}|${safeName}`;
    this.setItem(this.limitedDocumentKey, value);
    return {
      value,
      id,
      name: safeName
    };
  }

  clearLimitedDocument(): void {
    this.removeItem(this.limitedDocumentKey);
  }

  getSelectedExpertRole(): string | null {
    return this.getItem(this.selectedExpertRoleKey);
  }

  setSelectedExpertRole(code: string): void {
    this.setItem(this.selectedExpertRoleKey, code);
  }

  clearSelectedExpertRole(): void {
    this.removeItem(this.selectedExpertRoleKey);
  }

  /**
   * Borra TODO el localStorage salvo auth y preferencias críticas. Se invoca al crear o
   * limpiar una sesión de chat para que ningún estado colgado (rol experto, doc anclado,
   * sesión, rutas) sobreviva y la UI coincida con lo que realmente viaja al backend.
   */
  clearAllExceptCritical(): void {
    try {
      const preserved = new Map<string, string>();
      for (let index = 0; index < localStorage.length; index++) {
        const key = localStorage.key(index);
        if (!key || !this.isPreservedKey(key)) {
          continue;
        }
        const value = localStorage.getItem(key);
        if (value !== null) {
          preserved.set(key, value);
        }
      }
      localStorage.clear();
      preserved.forEach((value, key) => localStorage.setItem(key, value));
    } catch {
      // Storage failures should not break the chat UI.
    }
  }

  private isPreservedKey(key: string): boolean {
    return this.preservedKeys.includes(key)
      || this.preservedKeyPrefixes.some((prefix) => key.startsWith(prefix));
  }

  private getItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private setItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage failures should not break the chat UI.
    }
  }

  private removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage failures should not break the chat UI.
    }
  }
}
